import { useMemo } from 'react';
import { useActionGuard, ActionType, ResourceType } from '@hooks/useActionGuard';
import { constructPermissionResult } from '@hooks/useActionGuard/utils';
import { ApiAccount } from '@api';

/**
 * Custom hook that computes edit permissions for a list of destination accounts.
 * This hook pre-computes permissions at the component level to avoid calling hooks
 * inside loops, which violates React's Rules of Hooks.
 *
 * @param accounts - Array of accounts to check permissions for
 * @returns A map of account IDs to their edit permission results
 */
export const useEditAccountPermissions = (
  accounts: ApiAccount[],
  resourceType: 'destination' | 'source',
) => {
  const { canPerformAction } = useActionGuard();

  const editAccountPermission = constructPermissionResult(
    canPerformAction({
      new: {
        actionType: ActionType.CREATE,
        resource: {
          type:
            resourceType === 'destination' ? ResourceType.DESTINATION : ResourceType.EVENT_STREAM,
        },
      },
      old: { resourceType: 'workspace', action: 'edit' },
    }).isAllowed,
    `You do not have permission to edit ${resourceType} accounts.`,
  );

  const editPermissionsMap = useMemo(
    () => Object.fromEntries(accounts.map((account) => [account.id, editAccountPermission])),
    [accounts],
  );

  return { editPermissionsMap };
};
